// @ts-ignore
import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse, Method} from 'axios'

import {authAdapter} from '@/services/auth-adapter'

import sigV4Client from './sigV4Client'

export default class ApiGatewayClient {
  private readonly client: AxiosInstance

  constructor(axiosOpts?: AxiosRequestConfig) {
    this.client = axios.create(axiosOpts)
  }

  async sendRequest(req: ApiGatewayClientRequest): Promise<AxiosResponse<any>> {
    const {endpoint = 'https://localhost:9099', method, path, queryParams, data, signal} = req

    // Get session using AuthAdapter for unified authentication
    const session = await authAdapter.getCurrentSession()

    if (!endpoint) {
      throw Error(`Endpoint not provided for request: ${req.path}`)
    }

    if (!session.credentials) {
      throw Error('No AWS credentials available for request')
    }

    // Separate headers that should not be signed (custom headers that API Gateway doesn't validate)
    const unsignedHeaders: Record<string, string> = {}
    const headersToSign: Record<string, string> = {}

    if (req.headers) {
      Object.entries(req.headers).forEach(([key, value]) => {
        // Custom application headers should not be signed
        if (key.toLowerCase().startsWith('x-app-') || key.toLowerCase().startsWith('x-api-')) {
          unsignedHeaders[key] = value
        } else {
          headersToSign[key] = value
        }
      })
    }

    // Extract credentials from session
    const accessKey = session.credentials.accessKeyId
    const secretKey = session.credentials.secretAccessKey
    const sessionToken = session.credentials.sessionToken

    const signedRequest = await sigV4Client
      .newClient({
        accessKey,
        secretKey,
        sessionToken,
        region: 'eu-west-1',
        endpoint,
        clockDrift: 0,
      })
      .signRequest({
        method,
        path,
        headers: headersToSign,
        queryParams,
        body: data,
      })

    try {
      return await this.client.request({
        method,
        url: signedRequest.url,
        data,
        headers: {
          ...signedRequest.headers,
          'X-Id-Token': session.tokens?.idToken,
          ...unsignedHeaders,
        },
        signal,
      })
    } catch (e) {
      if (!signal?.aborted) throw e
      return Promise.reject()
    }
  }
}

interface ApiGatewayClientRequest {
  endpoint?: string
  method: Method
  path: string
  headers?: Record<string, string>
  queryParams?: Record<string, any>
  data?: any
  signal?: AbortSignal
}
